import { Card, Grid, Button, Stack, CircularProgress, Divider } from "@mui/material";
import GetAppIcon from '@mui/icons-material/GetApp';
import Layout from "components/Layout";
import StatisticHeader from "components/holdings/Statistic/StatisticHeader";
import PerformanceComparisonTable from "components/holdings/Statistic/PerformanceComparisonTable";
import PerformanceChart from "components/holdings/Statistic/PerformanceChart";
import { useState, useCallback, createContext, useContext } from "react";
import { StatisticState, ColumnPeriodT } from "helpers/types";
import { useStrategyQuery } from "hooks/useStrategyQuery";
import { useAccountQuery } from "hooks/useAccountQuery";
import IncomeSummary from "components/holdings/Statistic/IncomeSummary";
import OtherStatistic from "components/holdings/Statistic/OtherStatistic";
import { getLastStatisticDateRange, getLastStatisticPeriods } from "helpers";

// Create context for sharing trigger state
interface ReportTriggerContextType {
  shouldFetch: boolean;
  triggerFetch: () => void;
  columnPeriods: ColumnPeriodT[];
}

const ReportTriggerContext = createContext<ReportTriggerContextType | null>(null);

export const useReportTrigger = () => {
  const context = useContext(ReportTriggerContext);
  if (!context) {
    throw new Error('useReportTrigger must be used within ReportTriggerProvider');
  }
  return context;
};

const DEFAULT_STATE: StatisticState = {
  dateRange: getLastStatisticDateRange() as [string | null, string | null],
};

const ReportPage = () => {
  const [state, setState] = useState<StatisticState>(DEFAULT_STATE);
  const [columnPeriods, setColumnPeriods] = useState<ColumnPeriodT[]>(getLastStatisticPeriods());
  const [shouldFetch, setShouldFetch] = useState(false);

  const { data: stockData, isLoading: isStockLoading, isFetching: isStockFetching } = useStrategyQuery();
  const { data: accountData, isLoading: isAccountLoading, isFetching: isAccountFetching } = useAccountQuery();

  const handleStateChange = (newState: Partial<StatisticState>) => {
    setState(prev => ({ ...prev, ...newState }));
  };

  const handleColumnPeriodsChange = useCallback((periods: ColumnPeriodT[]) => {
    setColumnPeriods(periods);
    // Trigger fetch when periods change
    if (shouldFetch) {
      setShouldFetch(false);
      setTimeout(() => setShouldFetch(true), 0);
    }
  }, [shouldFetch]);

  const triggerFetch = useCallback(() => {
    setShouldFetch(true);
  }, []);

  const isLoading = isStockLoading || isStockFetching || isAccountLoading || isAccountFetching;

  return (
    <ReportTriggerContext.Provider value={{ shouldFetch, triggerFetch, columnPeriods }}>
      <Layout title="Report" obsolete>
        <Card elevation={2}>
          <Grid container>
            <Grid item xs={12} sx={{ padding: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <img src="/logo.svg" alt="logo" />
              <StatisticHeader
                state={state}
                onStateChange={handleStateChange}
              />
            </Grid>
            <Grid item xs={12} sx={{ padding: 2 }}>
              <Stack direction="row" justifyContent="flex-end" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                {isLoading ? (
                  <CircularProgress size={20} />
                ) : (
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<GetAppIcon />}
                    onClick={triggerFetch}
                    disabled={state.dateRange[0] === null || state.dateRange[1] === null}
                  >
                    Get
                  </Button>
                )}
              </Stack>
              <PerformanceComparisonTable
                dateRange={state.dateRange}
                onColumnPeriodsChange={handleColumnPeriodsChange}
              />
              <Divider sx={{ my: 2 }} />
              <PerformanceChart />
              <Divider sx={{ my: 2 }} />
              <IncomeSummary
                columnPeriods={columnPeriods}
              />
              <Divider sx={{ my: 2 }} />
              <OtherStatistic
                dateRange={state.dateRange}
                columnPeriods={columnPeriods}
              />
            </Grid>
          </Grid>
        </Card>
      </Layout>
    </ReportTriggerContext.Provider>
  );
};

export default ReportPage;
